import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MailingProofResult, MailingProofType, PreparedNoo } from '../../shared/api';
import SignaturePad, { type SignaturePadHandle } from '../../components/SignaturePad';
import { Banner, Button, ChoiceCard, DataTable, Field, FilePicker, Input, Pill, Steps, Td, Textarea } from '../../components/ui';
import { api, ApiError, fileUrl } from '../../lib/api';
import { fmtDate, fmtDateTime } from '../../lib/format';
import { useSession } from '../../lib/session';
import type { TabProps } from './types';
import { ADDRESS_HINT, looksLikeFullAddress } from '../../lib/address';

/**
 * Self-service mailing: the contractor prints the Notice to Owner, walks it to
 * the post office, and brings back what the counter gave them.
 *
 * The step the contractor is on is derived from the job, never from local
 * state, so a reload or a second device lands in the same place. Only that step
 * is shown — an upload box before the PDF exists is an invitation to a support
 * ticket.
 */
const STEP_LABELS = ['Choose method', 'Prepare notice', 'Mail it', 'Confirm delivery'];

type Step = 0 | 1 | 2 | 3;

function stepFor(job: TabProps['job']): Step {
  // Mailed is as far as the rail goes: the last step covers both "waiting on
  // the green card" and "delivery confirmed".
  if (job.nooSentAt) return 3;
  if (job.status === 'noo_prepared' || job.nooSendMethod === 'self_service') return 2;
  return 0;
}

export default function MailingTab({ job, reload }: TabProps) {
  const { can, selfServiceDisclaimer, contractor } = useSession();
  const missingAddress = !contractor?.address?.trim();
  const incompleteAddress = !missingAddress && !looksLikeFullAddress(contractor?.address);
  const hirerIncomplete = !looksLikeFullAddress(job.contractedWithAddress);
  const [hirerAddress, setHirerAddress] = useState(job.contractedWithAddress ?? '');
  const [hirerSaving, setHirerSaving] = useState(false);
  const [hirerError, setHirerError] = useState<string | null>(null);

  const saveHirer = async () => {
    setHirerError(null);
    if (!looksLikeFullAddress(hirerAddress)) {
      setHirerError(`Enter the full address. ${ADDRESS_HINT}.`);
      return;
    }
    setHirerSaving(true);
    try {
      await api.put(`/api/jobs/${job.id}/contracted-with`, { address: hirerAddress });
      await reload();
    } catch (err) {
      setHirerError(err instanceof ApiError ? err.message : 'Could not save the address');
    } finally {
      setHirerSaving(false);
    }
  };
  const sigRef = useRef<SignaturePadHandle>(null);
  const mayAct = can('manager');

  // Verbatim from the backend — the sentence the API refuses with is the
  // sentence shown. Null only while /api/me is still in flight.
  const disclaimer = selfServiceDisclaimer;

  const derived = stepFor(job);
  // The only local step: "I picked self-service but have not prepared yet."
  const [chose, setChose] = useState(false);
  // Prepared but not mailed: nothing has left the office, so the notice can be
  // corrected and prepared again (the server allows it until nooSentAt is set).
  const [redo, setRedo] = useState(false);
  const step: Step = derived === 0 && chose ? 1 : derived === 2 && redo ? 1 : derived;

  const [ack, setAck] = useState(false);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prepared, setPrepared] = useState<PreparedNoo | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [tracking, setTracking] = useState('');
  const [attestedDate, setAttestedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lastProof, setLastProof] = useState<MailingProofResult['proof'] | null>(null);

  // No fetch here: getJobDetail carries the proofs, so the page draws from one
  // response. GET /noo/proofs still exists for a targeted refresh, but reload()
  // re-reads the detail and is what runs after an upload.
  const proofs = job.mailingProofs;
  const receipt = proofs.find((p) => p.proofType === 'certified_mail_receipt') ?? null;
  const delivered = proofs.find((p) => p.proofType === 'delivery_confirmation') ?? null;
  // A receipt with no tracking number moves the card but will NOT satisfy the
  // Claim of Lien gate — see claim-eligibility.ts. Say so while it can be fixed.
  const untracked = !!receipt && !receipt.trackingNumber?.trim();

  const prepare = async () => {
    setError(null);
    const signatureImage = sigRef.current?.toDataUrl();
    if (!signatureImage) return setError('A signature is required on the notice.');
    setBusy(true);
    try {
      const res = await api.post<PreparedNoo>(`/api/jobs/${job.id}/noo/prepare`, {
        acknowledgesSelfServiceLiability: true,
        descriptionOfWork: description,
        contractAmount: amount ? Number(amount) : undefined,
        signatureImage,
      });
      setPrepared(res);
      setRedo(false);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not prepare the Notice to Owner');
    } finally {
      setBusy(false);
    }
  };

  const upload = async (proofType: MailingProofType) => {
    setError(null);
    if (!file) return setError('Choose the file you want to upload first.');
    setBusy(true);
    try {
      const form = new FormData();
      form.append('proofType', proofType);
      if (tracking.trim()) form.append('trackingNumber', tracking.trim());
      if (attestedDate) form.append('attestedDate', attestedDate);
      if (notes.trim()) form.append('notes', notes.trim());
      // Last: the backend reads the text fields off the file part.
      form.append('file', file);
      const res = await api.post<MailingProofResult>(`/api/jobs/${job.id}/noo/proof`, form);
      setLastProof(res.proof);
      setFile(null);
      setTracking('');
      setAttestedDate('');
      setNotes('');
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not upload the proof');
    } finally {
      setBusy(false);
    }
  };

  const pdfUrl = fileUrl(prepared?.pdfUrl ?? job.nooSelfServiceUrl ?? null);

  return (
    <div className="space-y-6">
      <Steps steps={STEP_LABELS} current={step} />

      {!mayAct && (
        <Banner tone="watch" title="Your seat cannot send notices">
          Preparing or evidencing a Notice to Owner needs a manager or admin seat.
        </Banner>
      )}
      {error && <Banner tone="urgent">{error}</Banner>}

      {step === 0 && (
        <section className="space-y-4">
          <h3 className="text-sm font-semibold text-ink">How should this notice go out?</h3>
          <div className="flex flex-col gap-3 sm:flex-row">
            <ChoiceCard
              title="Send through LienGuardian"
              badge={<Pill>Coming soon</Pill>}
              disabled
              description="We mail it certified, track delivery, and hold the evidence for you."
            />
            <ChoiceCard
              title="Self-service mailing"
              description="Print and mail the Notice to Owner yourself via USPS Certified Mail, then upload what comes back."
              onSelect={() => setChose(true)}
              disabled={!mayAct}
            />
          </div>
        </section>
      )}

      {step === 1 && (
        <section className="space-y-4">
          <h3 className="text-sm font-semibold text-ink">Prepare the notice</h3>
          {missingAddress && (
            <Banner tone="watch" title="Add your company mailing address first">
              The notice prints your company's address as the lienor, so it's required before preparing.{' '}
              <Link to="/settings" className="underline">
                Add it in Settings
              </Link>
              , then come back to this tab — what you've typed here will need re-entering.
            </Banner>
          )}
          {incompleteAddress && (
            <Banner tone="watch" title="Your company address is incomplete">
              The notice would print “{contractor?.address}”. It needs street, city, state and ZIP.{' '}
              <Link to="/settings" className="underline">
                Fix it in Settings
              </Link>
              .
            </Banner>
          )}
          {hirerIncomplete && (
            <Banner tone="watch" title={`${job.contractedWithName}'s address is incomplete`}>
              <p>A copy of the notice goes to whoever hired you, so their full mailing address is required.</p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <Input
                  aria-label="Hiring party address"
                  value={hirerAddress}
                  onChange={(e) => setHirerAddress(e.target.value)}
                  placeholder="2222 East Main St, Tampa, FL 33602"
                />
                <Button type="button" onClick={() => void saveHirer()} disabled={hirerSaving || !mayAct}>
                  {hirerSaving ? 'Saving…' : 'Save address'}
                </Button>
              </div>
              {hirerError && <p className="mt-2 text-sm">{hirerError}</p>}
            </Banner>
          )}
          <Field label="Description of work" hint="Printed on the notice, per F.S. 713.06.">
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Electrical rough-in and fixture installation"
            />
          </Field>
          <Field label="Contract amount (optional)" hint="Leave blank to use the job's contract amount.">
            <Input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <SignaturePad ref={sigRef} label="Signature" />

          {/* Never render a consent box without the sentence being consented to. */}
          <label className="flex items-start gap-3 rounded-lg border border-watch/40 bg-watch/10 p-4 text-sm text-ink">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0"
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
            />
            <span>{disclaimer}</span>
          </label>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
            <Button variant="ghost" onClick={() => setChose(false)} disabled={busy}>
              ← Back
            </Button>
            <Button onClick={prepare} disabled={busy || !ack || !disclaimer || description.trim().length < 3 || missingAddress || incompleteAddress || hirerIncomplete}>
              {busy ? 'Preparing…' : 'Prepare NTO'}
            </Button>
          </div>
        </section>
      )}

      {step >= 2 && (
        <section className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-sm font-semibold text-ink">Your Notice to Owner</h3>
            {pdfUrl && (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-semibold text-tint hover:underline"
              >
                Download the PDF
              </a>
            )}
            {step === 2 && !receipt && mayAct && (
              <Button
                type="button"
                variant="ghost"
                className="px-2 py-0.5 text-xs"
                onClick={() => {
                  setPrepared(null);
                  setRedo(true);
                }}
              >
                Found a mistake? Prepare it again
              </Button>
            )}
          </div>
          {step === 2 && !receipt && (
            <p className="text-xs text-muted">
              Not mailed yet, so you can still correct and re-prepare it. Once you upload the mailing receipt it's final.
            </p>
          )}

          {(prepared?.recipients?.length || job.recipients.length > 0) && (
            <DataTable head={['Recipient', 'Role', 'Mailing address']} empty="No recipients recorded yet.">
              {(prepared?.recipients ?? job.recipients.map((r) => ({ type: r.recipientType, name: r.name, address: r.address }))).map(
                (r) => (
                  <tr key={`${r.type}-${r.name}`} className="border-b border-border/60">
                    <Td className="font-medium">{r.name}</Td>
                    <Td className="text-muted">{r.type.replace('_', ' ')}</Td>
                    <Td className="text-xs text-muted">{r.address}</Td>
                  </tr>
                ),
              )}
            </DataTable>
          )}

          {untracked && (
            <Banner tone="urgent" title="This receipt has no tracking number">
              Your receipt was uploaded but has no tracking number. Without one, this mailing cannot be used to verify
              lien eligibility. You can add the tracking number later.
            </Banner>
          )}

          {lastProof && (
            <Banner tone="ok" title="Proof uploaded">
              {lastProof.proofType === 'certified_mail_receipt'
                ? 'Certified mail receipt recorded. The notice now counts as served from the date on the receipt.'
                : 'Delivery confirmation recorded.'}
            </Banner>
          )}

          {receipt && (
            <div className="rounded-lg border border-border bg-surface-alt/40 p-4 text-sm">
              <p className="font-semibold text-ink">Certified mail receipt</p>
              <p className="mt-1 text-muted">
                Mailed {fmtDate(receipt.attestedDate)} · tracking{' '}
                <span className="font-mono">{receipt.trackingNumber ?? '— none —'}</span>
              </p>
              {fileUrl(receipt.fileUrl) && (
                <a className="mt-1 inline-block text-tint hover:underline" href={fileUrl(receipt.fileUrl)!} target="_blank" rel="noreferrer">
                  View the uploaded receipt
                </a>
              )}
            </div>
          )}

          {delivered ? (
            <Banner tone="ok" title="Delivery confirmed">
              Delivered {fmtDate(delivered.attestedDate)}. Uploaded {fmtDateTime(delivered.uploadedAt)}.
            </Banner>
          ) : (
            mayAct && (
              <div className="space-y-4 rounded-lg border border-border bg-surface-alt/40 p-4">
                <p className="text-sm text-muted">
                  {receipt
                    ? 'Once you receive the green card or USPS tracking shows delivered, upload the delivery confirmation.'
                    : 'Mail your NTO via USPS Certified Mail. After mailing, upload a photo or scan of your certified mail receipt.'}
                </p>
                <FilePicker
                  label={receipt ? 'Delivery confirmation' : 'Certified mail receipt'}
                  accept="image/*,application/pdf"
                  file={file}
                  onPick={setFile}
                  hint="A photo or a scan. PDF and images both work."
                  disabled={busy}
                />
                <Field
                  label="Tracking number"
                  hint={
                    receipt
                      ? 'Optional.'
                      : 'Strongly recommended — a receipt without one will not satisfy the Claim of Lien evidence gate.'
                  }
                >
                  <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="9400 1118 9922 3…" />
                </Field>
                <Field
                  label={receipt ? 'Date delivered (optional)' : 'Date mailed (optional)'}
                  hint="The date on the paper, not today. Blank means today."
                >
                  <Input type="date" value={attestedDate} onChange={(e) => setAttestedDate(e.target.value)} />
                </Field>
                <Field label="Notes (optional)">
                  <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Mailed at the Titusville post office" />
                </Field>
                <Button
                  onClick={() => upload(receipt ? 'delivery_confirmation' : 'certified_mail_receipt')}
                  disabled={busy || !file}
                >
                  {busy ? 'Uploading…' : receipt ? 'Upload Confirmation' : 'Upload Receipt'}
                </Button>
              </div>
            )
          )}
        </section>
      )}
    </div>
  );
}
